import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Mail, 
  Link2, 
  Link2Off,
  RefreshCw,
  Search,
  ExternalLink,
  Inbox,
  AlertCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Leads() {
  const [gmailStatus, setGmailStatus] = useState(null);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [connecting, setConnecting] = useState(false);
  const [oauthMessage, setOauthMessage] = useState(null);

  useEffect(() => {
    // Check URL params for OAuth result
    const params = new URLSearchParams(window.location.search);
    if (params.get('gmail') === 'connected') {
      setOauthMessage({ type: 'success', text: 'Gmail connecté avec succès !' });
      window.history.replaceState({}, '', window.location.pathname);
      // Force reload to pick up new auth cookies set by the callback
      setTimeout(() => { window.location.reload(); }, 1500);
    } else if (params.get('error')) {
      const errorKey = params.get('error');
      const details = params.get('details');
      const errorMap = {
        'invalid_state': 'État OAuth introuvable. Veuillez réessayer en cliquant sur "Connecter Gmail".',
        'state_expired': 'La session OAuth a expiré (>30 min). Veuillez réessayer.',
        'missing_params': 'Paramètres manquants dans la réponse Google.',
        'google_access_denied': 'Accès refusé. Vous devez autoriser l\'application.',
        'token_exchange_failed': `Erreur lors de l'échange de token${details ? ': ' + decodeURIComponent(details) : '. Veuillez réessayer.'}`
      };
      const errorText = errorMap[errorKey] || `Erreur OAuth: ${errorKey.replace(/_/g, ' ')}${details ? ' - ' + decodeURIComponent(details) : ''}`;
      setOauthMessage({ type: 'error', text: errorText });
      window.history.replaceState({}, '', window.location.pathname);
    }
    checkGmailStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkGmailStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/oauth/gmail/status`, { withCredentials: true });
      setGmailStatus(data);
      
      if (data.connected) {
        fetchLeads();
        fetchStats();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  const connectGmail = async () => {
    setConnecting(true);
    try {
      const { data } = await axios.get(`${API}/oauth/gmail/connect`, { withCredentials: true });
      // Redirect to Google OAuth
      window.location.href = data.authorization_url;
    } catch (e) {
      setConnecting(false);
    }
  };

  const disconnectGmail = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir déconnecter Gmail ?')) return;
    try {
      await axios.delete(`${API}/oauth/gmail/disconnect`, { withCredentials: true });
      setGmailStatus({ connected: false });
      setLeads([]);
      setStats(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    }
  };

  const fetchLeads = async (forceRefresh = false) => {
    setLoadingLeads(true);
    try {
      const { data } = await axios.get(`${API}/leads`, { 
        withCredentials: true,
        params: { months: 6, refresh: forceRefresh || undefined }
      });
      setLeads(data.leads || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoadingLeads(false);
    }
  };

  const fetchStats = async () => {
    try {
      // Use pre-computed DB stats (same as Lead Analytics page) for accurate totals
      const { data } = await axios.get(`${API}/settings/leads/stats`, { 
        withCredentials: true,
        params: { months: 6 }
      });
      // Transform to same format expected by the stats cards
      const totalLeads = data.grand_total || data.total_matched || 0;
      const sourceStats = (data.by_source || []).map(s => ({
        source: s.source,
        count: s.total
      }));
      setStats({
        connected: true,
        total_leads: totalLeads,
        stats: sourceStats
      });
    } catch (e) {
      // Fallback to old endpoint if DB stats not available
      try {
        const { data } = await axios.get(`${API}/leads/stats`, { withCredentials: true });
        setStats(data);
      } catch (e2) {
        toast.error(e2.response?.data?.detail || "Erreur chargement stats");
      }
    }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.from?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.snippet?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesSource = sourceFilter === 'all' || lead.source === sourceFilter;
    
    return matchesSearch && matchesSource;
  });

  const uniqueSources = [...new Set(leads.map(l => l.source).filter(Boolean))];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]" data-testid="leads-loading">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-[#002FA7] rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="leads-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">
              Leads & Formulaires
            </h1>
            <p className="text-neutral-500 mt-1">
              {gmailStatus?.connected 
                ? `Connecté à ${gmailStatus.gmail_email}`
                : 'Connectez votre Gmail pour voir les formulaires'
              }
            </p>
          </div>
          {gmailStatus?.connected ? (
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => fetchLeads(true)}
                disabled={loadingLeads}
                data-testid="refresh-leads-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingLeads ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
              <Button 
                variant="outline" 
                onClick={disconnectGmail}
                className="text-red-600 hover:text-red-700"
                data-testid="disconnect-gmail-btn"
              >
                <Link2Off className="w-4 h-4 mr-2" />
                Déconnecter
              </Button>
            </div>
          ) : (
            <div className="flex gap-2 items-center">
              {gmailStatus?.expired && (
                <span className="text-amber-600 text-sm">Token expire</span>
              )}
              <Button 
                className="bg-[#002FA7] hover:bg-[#002277] text-white"
                onClick={connectGmail}
                disabled={connecting}
                data-testid="connect-gmail-btn"
              >
                {connecting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Connexion...
                  </>
                ) : (
                  <>
                    <Link2 className="w-4 h-4 mr-2" />
                    {gmailStatus?.expired ? 'Reconnecter Gmail' : 'Connecter Gmail'}
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* OAuth Message */}
        {oauthMessage && (
          <div className={`p-4 rounded flex items-center gap-3 ${
            oauthMessage.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`} data-testid="oauth-message">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm font-medium">{oauthMessage.text}</p>
            <button 
              onClick={() => setOauthMessage(null)} 
              className="ml-auto text-neutral-400 hover:text-neutral-600"
            >x</button>
          </div>
        )}

        {/* Stats Cards */}
        {gmailStatus?.connected && stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border border-neutral-200" data-testid="leads-stats">
            <div className="p-6 border-r border-b border-neutral-200 md:border-b-0">
              <p className="text-xs font-mono uppercase tracking-wider text-neutral-500">
                Total Leads (6 mois)
              </p>
              <p className="font-mono text-4xl font-bold text-neutral-900 mt-2">
                {stats.total_leads || 0}
              </p>
            </div>
            {stats.stats?.slice(0, 3).map((stat, index) => (
              <div 
                key={stat.source} 
                className={`p-6 ${index < 2 ? 'border-r border-neutral-200' : ''}`}
              >
                <p className="text-xs font-mono uppercase tracking-wider text-neutral-500">
                  {stat.source}
                </p>
                <p className="font-mono text-4xl font-bold text-[#002FA7] mt-2">
                  {stat.count}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Not Connected State */}
        {!gmailStatus?.connected && (
          <div className="bg-white border border-neutral-200 p-12 text-center" data-testid="gmail-not-connected">
            <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-neutral-400" />
            </div>
            <h2 className="font-heading font-semibold text-xl text-neutral-900 mb-2">
              Connectez votre Gmail
            </h2>
            <p className="text-neutral-500 max-w-md mx-auto mb-6">
              Connectez votre compte Gmail pour récupérer automatiquement les formulaires 
              de contact et les leads provenant de vos différentes plateformes.
            </p>
            <Button 
              className="bg-[#002FA7] hover:bg-[#002277] text-white"
              onClick={connectGmail}
              disabled={connecting}
              data-testid="connect-gmail-btn-main"
            >
              {connecting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Connexion en cours...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Connecter avec Google
                </>
              )}
            </Button>
            <p className="text-xs text-neutral-400 mt-4">
              Nous n'accéderons qu'en lecture seule à vos emails
            </p>
          </div>
        )}

        {/* Connected - Leads List */}
        {gmailStatus?.connected && (
          <>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <Input
                  placeholder="Rechercher dans les leads..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-leads-input"
                />
              </div>
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="w-[200px]" data-testid="filter-source-select">
                  <SelectValue placeholder="Toutes les sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les sources</SelectItem>
                  {uniqueSources.map((source) => (
                    <SelectItem key={source} value={source}>{source}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Leads Table */}
            <div className="bg-white border border-neutral-200 overflow-hidden">
              {loadingLeads ? (
                <div className="p-8 text-center">
                  <div className="w-8 h-8 border-2 border-neutral-300 border-t-[#002FA7] rounded-full animate-spin mx-auto" />
                </div>
              ) : filteredLeads.length === 0 ? (
                <div className="p-12 text-center" data-testid="no-leads">
                  <Inbox className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
                  <p className="text-neutral-500">Aucun lead trouvé</p>
                  <p className="text-sm text-neutral-400 mt-1">
                    Les formulaires de contact apparaîtront ici
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-neutral-100" data-testid="leads-list">
                  {filteredLeads.map((lead) => (
                    <div 
                      key={lead.id} 
                      className={`p-4 hover:bg-neutral-50 transition-colors ${!lead.is_read ? 'bg-blue-50/50' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {!lead.is_read && (
                              <span className="w-2 h-2 bg-[#002FA7] rounded-full" />
                            )}
                            <span className="font-medium text-neutral-900 truncate">
                              {lead.subject || '(Sans sujet)'}
                            </span>
                          </div>
                          <p className="text-sm text-neutral-600 truncate">
                            {lead.from}
                          </p>
                          <p className="text-sm text-neutral-400 mt-1 line-clamp-2">
                            {lead.snippet}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <span className={`text-xs font-mono px-2 py-1 rounded ${
                            lead.is_form_submission 
                              ? 'bg-green-50 text-green-700' 
                              : 'bg-neutral-100 text-neutral-600'
                          }`}>
                            {lead.source || 'Email'}
                          </span>
                          {lead.date && (
                            <p className="font-mono text-xs text-neutral-500 mt-2">
                              {format(new Date(lead.date), 'dd MMM HH:mm', { locale: fr })}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
